import { getDiscoverBoardForStates } from "@nimble/candidate-api/discover-board-filters";
import { UserId } from "@nimble/datamodel/types/userId";
import { getInnerQueryData } from "@nimble/react-requests/data/response-utils";
import { Bookmark } from "lucide-react";
import { UseFormReturn } from "react-hook-form";

import { FilterValues } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import {
  ActiveSelection,
  SavedSearch,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/active-selection";
import {
  RowStatus,
  SavedFiltersRow,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/preferences-filter";
import { SavedSearchRowButtonDropdown } from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/saved-search-row-button-dropdown";
import {
  summarizeFilters,
  toFilterValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/utils";
import {
  toSavedSearchesParams,
  useUserSavedSearches,
} from "@/hooks/data/use-user-saved-searches";
import useStateConfig from "@/hooks/use-state-config";
import { jobFiltersTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";
import routes, { getAbsoluteUrl } from "@/lib/routes";
import statesConfig, { StateConfig } from "@/lib/states-config";

const getShareStateConfig = (search: SavedSearch): StateConfig =>
  statesConfig[getDiscoverBoardForStates(search.filters.states)];

const rowStatus = (isActive: boolean): RowStatus => {
  if (isActive) return RowStatus.Checked;
  return RowStatus.Unchecked;
};

const buildShareHref = (search: SavedSearch): string => {
  return getAbsoluteUrl(
    routes.getJobsStateWithFilters({
      state: getShareStateConfig(search),
      filters: toFilterValues(search.filters),
    }),
  );
};

export interface SavedSearchesSidebarListProps {
  userId: UserId;
  form: UseFormReturn<FilterValues>;
  filterDefaults: FilterValues;
  activeSelection: ActiveSelection;
  onDelete: (search: SavedSearch) => void;
}

export const SavedSearchesSidebarList: React.FC<
  SavedSearchesSidebarListProps
> = ({ userId, form, filterDefaults, activeSelection, onDelete }) => {
  const abbreviation = useStateConfig().abbreviation;
  const params = toSavedSearchesParams(abbreviation);

  const query = useUserSavedSearches(userId, params);
  const savedSearches = getInnerQueryData(query)?.searches;

  return (
    <div>
      {savedSearches?.map((search) => {
        const isActive =
          activeSelection.kind === "savedSearch" &&
          activeSelection.uuid === search.uuid;

        return (
          <SavedFiltersRow
            key={search.uuid}
            icon={<Bookmark className="stroke-ink-subtle size-4" />}
            title={search.name}
            subtitle={summarizeFilters(toFilterValues(search.filters))}
            onToggle={(next) => {
              const target = next
                ? toFilterValues(search.filters)
                : filterDefaults;
              form.reset(target);
            }}
            status={rowStatus(isActive)}
            testId={jobFiltersTestIds.SAVED_SEARCHES_LIST_ROW}
            trailing={
              <div className="opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                <SavedSearchRowButtonDropdown
                  search={search}
                  shareHref={buildShareHref(search)}
                  onDelete={onDelete}
                />
              </div>
            }
          />
        );
      })}
    </div>
  );
};
